import Video from '@atoms/Video/Video';
import {
  autoPlayVimeoPlayerAttributes,
  autoPlayVimeoPlayerConfig,
  autoPlayYoutubePlayerAttributes,
  autoPlayYoutubePlayerConfig,
  standardViemoPlayerConfig,
  standardVimeoPlayerAttributes,
  standardYoutubePlayerAttributes,
  standardYoutubePlayerConfig,
} from '@config/video.config';
import VideoProviderEnum from '@models/enums/VideoProviderEnum';
import IExternalVideo from '@models/IExternalVideo';
import { IVideoAttributes } from '@models/IVideoAttributes';
import classNames from 'classnames';
import { forwardRef, PropsWithChildren } from 'react';

interface Props extends IExternalVideo {
  readonly autoPlay: boolean;
  readonly onEndedVideo?: () => void;
  readonly onVideoReady?: () => void;
  readonly onVideoStart?: () => void;
  readonly classes?: string;
  readonly customVideoAttributes?: IVideoAttributes;
  readonly isInvisible?: boolean;
  readonly dataTestId?: string;
}

const ExternalVideo = forwardRef(
  (
    {
      videoProvider = VideoProviderEnum.Youtube,
      videoId,
      autoPlay,
      onEndedVideo,
      onVideoReady,
      onVideoStart,
      classes,
      children,
      title,
      customVideoAttributes,
      isInvisible = false,
      dataTestId = 'External-Video',
    }: PropsWithChildren<Props>,
    ref
  ) => {
    const options = {
      youtube: {
        autoPlay: {
          videoAttributes: autoPlayYoutubePlayerAttributes,
          videoConfig: autoPlayYoutubePlayerConfig,
        },
        standard: {
          videoAttributes: standardYoutubePlayerAttributes,
          videoConfig: standardYoutubePlayerConfig,
        },
      },
      vimeo: {
        autoPlay: {
          videoAttributes: autoPlayVimeoPlayerAttributes,
          videoConfig: autoPlayVimeoPlayerConfig,
        },
        standard: {
          videoAttributes: standardVimeoPlayerAttributes,
          videoConfig: standardViemoPlayerConfig,
        },
      },
    };

    return (
      <div
        className={classNames('relative h-full w-full', {
          invisible: isInvisible,
        })}
        data-testid={dataTestId}
      >
        <Video
          videoSource={videoId}
          videoAttributes={{
            ...options[videoProvider]?.[`${autoPlay ? 'autoPlay' : 'standard'}`]
              ?.videoAttributes,
            ...customVideoAttributes,
          }}
          config={
            options[videoProvider]?.[`${autoPlay ? 'autoPlay' : 'standard'}`]
              ?.videoConfig
          }
          onEndedVideo={onEndedVideo}
          onVideoReady={onVideoReady}
          onVideoStart={onVideoStart}
          classes={classes}
          title={title}
          ref={ref}
        />
        {children}
      </div>
    );
  }
);

export default ExternalVideo;
